/**
 * Utility functions for ModuleRevenue component
 */

import { formatDistanceToNow } from "date-fns";
import { it } from "date-fns/locale";

/**
 * Format a number as Italian currency (EUR)
 * @param {number} value - The value to format
 * @returns {string} Formatted currency string (e.g., "€ 1.234,56")
 */
export const formatCurrency = (value) => {
  if (value == null || isNaN(value)) return "€ 0,00";
  return new Intl.NumberFormat("it-IT", {
    style: "currency",
    currency: "EUR",
  }).format(value);
};

/**
 * Format a decimal as percentage
 * @param {number} value - The decimal value (e.g., 0.65)
 * @returns {string} Formatted percentage string (e.g., "65%")
 */
export const formatPercentage = (value) => {
  if (value == null || isNaN(value)) return "0%";
  return `${Math.round(value * 100)}%`;
};

/**
 * Format a date relative to now in Italian
 * @param {Date|string} date - The date to format
 * @returns {string} Relative time string (e.g., "2 ore fa")
 */
export const formatRelativeTime = (date) => {
  if (!date) return "-";
  const dateObj = typeof date === "string" ? new Date(date) : date;
  if (isNaN(dateObj.getTime())) return "-";
  return formatDistanceToNow(dateObj, { addSuffix: true, locale: it });
};

/**
 * Format a number with Italian locale (thousand separators)
 * @param {number} value - The value to format
 * @returns {string} Formatted number string (e.g., "1.234")
 */
export const formatNumber = (value) => {
  if (value == null || isNaN(value)) return "0";
  return new Intl.NumberFormat("it-IT").format(value);
};

/**
 * Export data as CSV file
 * @param {Array} data - Array of module revenue objects
 * @param {string} filename - Optional filename (without extension)
 */
export const downloadCSV = (data, filename = "module_revenue") => {
  if (!data || data.length === 0) return;

  const headers = [
    "Modulo",
    "SKU",
    "MRR",
    "ARR",
    "Abbonamenti Attivi",
    "Abbonati Mensili",
    "Abbonati Annuali",
    "Prezzo Medio",
    "Quantità Totale",
    "Linea Prodotto",
  ];

  const rows = data.map((item) => [
    item.module_name || "",
    item.module_sku || "",
    item.total_mrr || 0,
    item.total_arr || 0,
    item.active_subscriptions_count || 0,
    item.monthly_subscribers_count || 0,
    item.yearly_subscribers_count || 0,
    item.average_price || 0,
    item.total_quantity || 0,
    item.is_product_line ? "Sì" : "No",
  ]);

  const csvContent = [
    headers.join(";"),
    ...rows.map((row) =>
      row
        .map((cell) => {
          // Escape quotes and wrap in quotes if contains separator or quotes
          const cellStr = String(cell);
          if (cellStr.includes(";") || cellStr.includes('"') || cellStr.includes("\n")) {
            return `"${cellStr.replace(/"/g, '""')}"`;
          }
          return cellStr;
        })
        .join(";")
    ),
  ].join("\n");

  // Add BOM for Excel compatibility with UTF-8
  const BOM = "\uFEFF";
  const blob = new Blob([BOM + csvContent], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.setAttribute("href", url);
  link.setAttribute("download", `${filename}_${new Date().toISOString().split("T")[0]}.csv`);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

/**
 * Calculate aggregated KPIs from module data
 * @param {Array} data - Array of module revenue objects
 * @returns {Object} Aggregated KPIs
 */
export const calculateKPIs = (data) => {
  if (!data || data.length === 0) {
    return {
      totalMRR: 0,
      totalARR: 0,
      totalSubscriptions: 0,
      monthlyCount: 0,
      yearlyCount: 0,
      monthlySplit: 0,
      yearlySplit: 0,
    };
  }

  const totalMRR = data.reduce((sum, item) => sum + (item.total_mrr || 0), 0);
  const totalARR = data.reduce((sum, item) => sum + (item.total_arr || 0), 0);
  const totalSubscriptions = data.reduce(
    (sum, item) => sum + (item.active_subscriptions_count || 0),
    0
  );
  const monthlyCount = data.reduce(
    (sum, item) => sum + (item.monthly_subscribers_count || 0),
    0
  );
  const yearlyCount = data.reduce(
    (sum, item) => sum + (item.yearly_subscribers_count || 0),
    0
  );

  const totalSubs = monthlyCount + yearlyCount;
  const monthlySplit = totalSubs > 0 ? monthlyCount / totalSubs : 0;
  const yearlySplit = totalSubs > 0 ? yearlyCount / totalSubs : 0;

  return {
    totalMRR,
    totalARR,
    totalSubscriptions,
    monthlyCount,
    yearlyCount,
    monthlySplit,
    yearlySplit,
  };
};

/**
 * Prepare chart data for the donut chart
 * @param {Array} data - Array of module revenue objects
 * @param {string} metric - "mrr" or "subscriptions"
 * @param {Array} colors - Array of chart colors
 * @returns {Object} Chart.js data object
 */
export const prepareDonutChartData = (data, metric = "mrr", colors) => {
  if (!data || data.length === 0) {
    return {
      labels: [],
      datasets: [{ data: [], backgroundColor: [] }],
    };
  }

  // Sort by the selected metric
  const sortedData = [...data].sort((a, b) => {
    const valueA = metric === "mrr" ? a.total_mrr || 0 : a.active_subscriptions_count || 0;
    const valueB = metric === "mrr" ? b.total_mrr || 0 : b.active_subscriptions_count || 0;
    return valueB - valueA;
  });

  // Take top 10 and aggregate the rest as "Altri"
  const top10 = sortedData.slice(0, 10);
  const others = sortedData.slice(10);

  const labels = top10.map((item) => item.module_name || "N/A");
  const values = top10.map((item) =>
    metric === "mrr" ? item.total_mrr || 0 : item.active_subscriptions_count || 0
  );
  const backgroundColors = top10.map((_, index) => colors[index % colors.length]);

  // Add "Altri" if there are more than 10 modules
  if (others.length > 0) {
    const othersValue = others.reduce(
      (sum, item) =>
        sum + (metric === "mrr" ? item.total_mrr || 0 : item.active_subscriptions_count || 0),
      0
    );
    labels.push("Altri");
    values.push(othersValue);
    backgroundColors.push("#CCCCCC");
  }

  return {
    labels,
    datasets: [
      {
        data: values,
        backgroundColor: backgroundColors,
        borderWidth: 1,
        borderColor: "#ffffff",
      },
    ],
  };
};
